import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "./ToastProvider";

function ProductCard({
  accent,
  product,
  onClick,
  onAddToList,
  retailerLabel,
  showComparison = true,
}) {
  const [addBusy, setAddBusy] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const normalizePlatformKey = (platform) => {
    const p = String(platform || "").trim().toLowerCase();
    if (p === "morrisons") return "morrison";
    if (p === "sainsburry") return "sainsbury";
    if (p === "sainsburys") return "sainsbury";
    return p;
  };
  const parsePrice = (value) => {
    if (value == null) return null;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    const normalized = String(value).trim().replace(",", ".").replace(/[^0-9.-]/g, "");
    if (!normalized) return null;
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const platformMeta = {
    tesco: { label: "Tesco", color: "#1359EF" },
    morrison: { label: "Morrisons", color: "#A20886" },
    asda: { label: "Asda", color: "#08A237" },
    sainsbury: { label: "Sainsbury's", color: "#EC570D" },
  };
  const knownPlatformKey = normalizePlatformKey(product?.platform);
  const knownStoreMeta = platformMeta[knownPlatformKey] || null;

  const retailerLabelToPlatformKey = (label) => {
    const l = String(label || "").trim().toLowerCase();
    if (l.includes("tesco")) return "tesco";
    if (l.includes("morris")) return "morrison";
    if (l.includes("asda")) return "asda";
    if (l.includes("sainsbury")) return "sainsbury";
    return "";
  };

  const effectivePlatformKey = retailerLabel
    ? retailerLabelToPlatformKey(retailerLabel)
    : knownPlatformKey;

  const comparisonSource =
    Array.isArray(product?.comparison) && product.comparison.length > 0
      ? product.comparison
      : ["tesco", "morrison", "asda", "sainsbury"].map((platform) => ({
          platform,
          price: platform === effectivePlatformKey ? product?.price : null,
        }));

  const comparisonRows = comparisonSource.map((item) => {
    const platform = normalizePlatformKey(
      item?.platform || item?.retailer || item?.provider || item?.store || item?.site
    );
    const meta = platformMeta[platform] || {
      label: item?.platform || item?.retailer || item?.provider || item?.store || "Store",
      color: "#94A3B8",
    };
    const numericPrice = parsePrice(
      item?.price ?? item?.unit_price ?? item?.price_per_unit ?? item?.offer_price
    );
    return {
      platform,
      store: meta.label,
      price: Number.isFinite(numericPrice) ? numericPrice : null,
      formatted: Number.isFinite(numericPrice) ? `£${numericPrice.toFixed(2)}` : "N/A",
      colorHex: meta.color,
    };
  });

  const knownPrices = comparisonRows
    .map((r) => r.price)
    .filter((p) => typeof p === "number" && Number.isFinite(p));

  const cheapestPrice = knownPrices.length ? Math.min(...knownPrices) : null;
  const cheapestRow =
    cheapestPrice == null
      ? null
      : comparisonRows.find((r) => r.price === cheapestPrice) || null;

  const highestPrice = knownPrices.length ? Math.max(...knownPrices) : null;
  const potentialSavings =
    cheapestPrice != null && highestPrice != null ? highestPrice - cheapestPrice : null;
  const savings =
    typeof potentialSavings === "number" && Number.isFinite(potentialSavings) && potentialSavings > 0
      ? potentialSavings
      : null;

  const maxComparisonPrice = comparisonRows.reduce((max, row) => {
    if (typeof row.price === "number" && Number.isFinite(row.price)) {
      return Math.max(max, row.price);
    }
    return max;
  }, 0);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick?.();
      }}
      className="flex flex-col rounded-[18px] border border-slate-200 bg-white text-left shadow-sm transition-shadow hover:shadow-md focus:outline-none"
    >
      <div className="flex-1 p-4">
        {/* Pack size pill at top-left (previous design) */}
        <div className="mb-2 flex justify-start">
          <span className="inline-flex rounded-full bg-orange-50 px-2 py-0.5 text-[10px] font-semibold text-orange-600">
            {product?.packsize || ""}
          </span>
        </div>

        {/* Image */}
        <div className="mb-4 flex h-28 items-center justify-center rounded-xl bg-[#F5F7FB]">
          {product?.images?.[0] ? (
            <img
              src={product.images[0]}
              alt={product.title || product.sku}
              className="h-full object-contain"
            />
          ) : (
            <span className="text-xs text-slate-400">No Image</span>
          )}
        </div>

        {/* Name */}
        <p className="text-[14px] font-bold text-[#24303A] line-clamp-2 h-6">
          {product?.title || product?.sku || "Easy Wring Turbo"}
        </p>

        {showComparison ? (
          <>
            {/* Retailer comparison line (previous design placement) */}
            

            {/* Cheapest pill (new text, previous layout) */}
            <p className="mt-2 inline-flex rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-700">
              Cheapest at {cheapestRow?.store || "—"}{" "}
              {cheapestRow?.formatted || "N/A"}
            </p>
            <div className="mt-2 flex items-center justify-between text-[11px] font-semibold text-[#6E7B86]">
              {savings != null ? <span>Save £{savings.toFixed(2)} vs Highest Price</span> : <span>&nbsp;</span>}
            </div>

            <div className="mt-2 space-y-1 text-[11px]">
              <div className="flex justify-between font-semibold text-[#24303A]">
                {comparisonRows.map((row) => (
                  <span key={row.platform}>{row.formatted}</span>
                ))}
              </div>

              <div className="flex items-end justify-between gap-1">
                {comparisonRows.map((row) => (
                  <div key={row.platform} className="flex-1">
                    <div
                      className="mx-auto w-full rounded-t-md"
                      style={{
                        backgroundColor: row.colorHex,
                        height:
                          row.price == null || maxComparisonPrice <= 0
                            ? 10
                            : Math.max(
                                10,
                                Math.round((row.price / maxComparisonPrice) * 24)
                              ),
                      }}
                    />
                  </div>
                ))}
              </div>

              {/* Store labels row (previous design sizing) */}
              <div className="flex justify-between text-[11px] text-[#6E7B86]">
                {comparisonRows.map((row) => (
                  <span key={row.platform}>{row.store}</span>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="mt-3">
           
            <div className="mt-2 flex rounded-full justify-between bg-[#7352C7] px-3 py-1 text-[11px] font-semibold text-emerald-700">
              <span className="text-[13px] font-bold text-[#fff]">
                {retailerLabel || knownStoreMeta?.label || "Retailer"}
              </span>
              <span className="text-[13px] font-extrabold text-[#fff]">
                {comparisonRows.find((r) => r.platform === effectivePlatformKey)?.formatted ||
                  cheapestRow?.formatted ||
                  "£—"}
              </span>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 gap-2 mt-2 justify-center">
          <button
            type="button"
            disabled={addBusy}
            className="rounded-full bg-[#fff] px-3 py-2 text-xs font-extrabold text-black border border-slate-700 border-2 shadow-sm"
            onClick={async (e) => {
              e.stopPropagation();
              if (typeof onAddToList !== "function" || addBusy) return;
              setAddBusy(true);
              try {
                await onAddToList(product);
                showToast({
                  type: "success",
                  title: "Added to list",
                  message: "Product added to grocery list.",
                  actionLabel: "Go to Grocery List",
                  onAction: () => {
                    window.location.assign("/settings?tab=grocery&view=lists");
                  },
                });
              } catch (err) {
                const errorMessage = err?.message || "Failed to add product to list.";
                if (errorMessage === "ADD_TO_LIST_CANCELLED" || err?.code === "ADD_TO_LIST_CANCELLED") {
                  return;
                }
                const isNoListError = /No grocery list found/i.test(errorMessage);
                showToast({
                  type: "error",
                  title: "Add failed",
                  message: errorMessage,
                  actionLabel: isNoListError ? "Create list" : "",
                  onAction: isNoListError
                    ? () => navigate("/settings?tab=grocery&view=create")
                    : null,
                });
              } finally {
                setAddBusy(false);
              }
            }}
          >
            {addBusy ? "Adding..." : "Add to List"}
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 inline-block align-middle ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14M13 6l6 6-6 6"></path></svg>
          </button>
        </div>
      </div>

    </div>
  );
}

export default ProductCard;